import { Project } from './project';
import { RootState } from '../../store/root-state';
import {
  IPhysicalBuilding,
  IPhysicalFloor,
  IPhysicalLocation,
  IPlanHolder,
  IProjectUser,
} from '../../api-client/autogenerated';
import { getProjectUsers, shouldDisplayUser } from '../../scripts/utils';

export const getProjectState = (state: RootState): Project => state.project.project;

/**
 * Whether the project currently open is archived, and therefore view-only: it can be read by
 * anyone who could open it before it was archived, but the API refuses every write against it and
 * records none of the activity a read normally would. The flag is nullable, so only a positively
 * set value counts -- treating an unknown project as archived would grey out a live one.
 */
export const getProjectIsArchived = (state: RootState): boolean =>
  !!state.project.project?.isArchived;

export const getDocumentsAssignedToMeState = (state: RootState) =>
  state.project.documentsAssignedToMeCount;

export const getProjectId = (state: RootState) =>
  state.project.project?.id ?? state.document?.document?.projectId;

export const getProjectUsersState = (state: RootState): IProjectUser[] => {
  if (state.project.project?.projectUserList) {
    return getProjectUsers(state.project.project, false).filter(
      (x) =>
        x.securityGroupId !== null ||
        x.user?.isSiteAdmin ||
        (x.user?.adminOfSubscriberId &&
          x.user.adminOfSubscriberId === state.project.project?.subscriberId),
    );
  }
  return [];
};

/**
 * The only lists that include disabled users, and they all SHOW them rather
 * than offer them:
 *
 *   Project Directory        -- greyed, with a red [Disabled] label. The
 *                               directory is the record of who is on the
 *                               project, and somebody disabled is still part
 *                               of that record.
 *   Add/Manage Project       -- same row component, with the selection
 *                               checkbox disabled, so they can be seen and
 *                               removed but never selected.
 *   Construction Setup       -- its Project Users table only, for the same
 *                               reason. Its dropdowns stay on
 *                               getProjectUsersState.
 *
 * Everywhere else filters them out entirely -- pickers, the Construction Setup
 * dropdowns, filters, notification lists -- via getProjectUsersState. The rule
 * is that a disabled user is never selectable for an assignment or
 * association; being absent is the usual way of achieving that, and these are
 * the exception.
 *
 * Named for what it returns rather than who calls it. It was
 * getAdminProjectUsersState, which read as "the list for admin screens" and
 * was used by two setup pages on exactly that reading.
 *
 * To show the name of a user already recorded on a document, use
 * getProjectUsersForDisplayState instead -- that is resolution, not listing.
 */
export const getProjectUsersIncludingDisabledState = (state: RootState) => {
  if (state.project.project?.projectUserList) {
    return getProjectUsers(state.project.project, true).filter(
      (x) =>
        x.securityGroupId !== null ||
        x.user?.isSiteAdmin ||
        (x.user?.adminOfSubscriberId &&
          x.user.adminOfSubscriberId === state.project.project?.subscriberId),
    );
  }
  return [];
};

/**
 * Unfiltered, and only for resolving a user who is ALREADY on the record --
 * the responsible architect on a document, a name on a distribution list.
 *
 * The filtered selectors are the right source for offering a choice and the
 * wrong one for reporting a fact: a print view that looks up its responsible
 * architect in a list with disabled users removed finds nothing and prints a
 * blank where the name should be. Never use this to build a list somebody
 * picks from.
 */
export const getProjectUsersForDisplayState = (state: RootState): IProjectUser[] =>
  state.project.project?.projectUserList ?? [];

export const getAllProjectUsersState = (state: RootState): IProjectUser[] => {
  return (
    state.project.project?.projectUserList?.filter(
      (x) => x.user && shouldDisplayUser(x.user, false),
    ) || []
  );
};

export const getAllPlanHoldersState = (state: RootState): IPlanHolder[] => {
  // Disabled users drop out here the same as everywhere else. This filtered on
  // `x.user` alone and never consulted shouldDisplayUser, so the plan holder
  // log was the one list a disabled user still appeared in unmarked.
  return (
    state.project.project?.planHolderList?.filter(
      (x) => x.user && shouldDisplayUser(x.user, false),
    ) || []
  );
};

export const getCurrentProjectUser = (state: RootState) =>
  state.project.project?.projectUserList?.find(
    (projectUser) => projectUser.userId === state.user.user.id,
  );

export const getCurrentProjectSubscriberId = (state: RootState) =>
  state.project.project?.subscriberId;

export const getCurrentSubscriberLogoFileId = (state: RootState) =>
  state.project.project?.subscriber?.logoFileId;

export const getDefaultArchitect = (state: RootState) =>
  state.project.project?.architectAdministratorUser;

export const getDefaultContractor = (state: RootState) =>
  state.project.project?.contractorAdministratorUser;

export const getPhysicalBuildingsState = (state: RootState): IPhysicalBuilding[] =>
  state.project.project?.physicalBuildings || [];

export const getPhysicalFloorsState = (state: RootState): IPhysicalFloor[] =>
  getPhysicalBuildingsState(state)
    .map((building) => building.floors || [])
    .flat();

export const getPhysicalLocationsState = (state: RootState): IPhysicalLocation[] =>
  getPhysicalFloorsState(state)
    .map((floor) => floor.locations || [])
    .flat();

export const getDisableProcoreIntegrationFeatures = (state: RootState) =>
  !!state.project?.project?.isProcoreIntegrationEnabled;
